'use client';

/**
 * ONE rule for the name a workspace item shows on the host surfaces that list
 * items: the rail taskbar puck and its hover card, the Stage sheet, the ⌘⇧←/→
 * switcher strip, the dock, the workspace search results and the panel Search
 * view, the Collaborate panel's "in <app>" line, the desktop screens overview
 * and every canvas menu that names a window or a screen (Pick layer, Move to
 * screen, Go to screen, Share), the edge-swipe hint, and the plain-text
 * summary a canvas copy puts on the clipboard.
 *
 * WHY (the 2026-09-13 browser pass). The Revent app was renamed from
 * "Revent". Inside the app, its tile and its tab said "Revent", but the rail
 * still said "Revent": the hover card read "Revent · App · @robutlerai",
 * the puck was "Go to Revent" and the screens overview named it "Revent".
 * Every one of those surfaces read `item.state.cache.displayName`, a snapshot
 * copied into the SHARED workspace item when the window was created, and
 * nothing ever refreshes it. Each surface also carried its own copy of that
 * lookup chain, so patching one would have left the others disagreeing with
 * it. Net effect: a renamed app or document kept its old name in every
 * workspace that already had it open. The first pass moved the rail, Stage
 * sheet, switcher, dock, search and clipboard; the desktop overview, the
 * canvas menus, the swipe hint and the Search / Collaborate panels still read
 * the snapshot, so on desktop the overview kept saying "Revent" beside a
 * rail that said "Revent". They resolve here now too.
 *
 * THE RULE: live content metadata first, the stored snapshot only while no
 * live metadata has arrived. "Live" is the store's `contentCache` slice, keyed
 * by content id. The server seeds it from the content row on every page load
 * (`loadWorkspaceBundle` in lib/workspaces/queries.ts), each ContentNode
 * refreshes it through `seedContent` when its `/info` round-trip returns, and
 * a rename made in this tab refreshes it through `announceContentRenamed`
 * (below). It is this tab's own state and never enters the Y.Doc.
 *
 * READ-SIDE ONLY, ON PURPOSE. Never "fix" a stale label by writing the fresh
 * name into `item.state.cache` or anywhere else in the shared workspace
 * document. On 2026-09-13 a change that wrote shared item state from every
 * client looped forever between two tabs. Any per-client write of a derived
 * value has that shape: two tabs that loaded at different times hold different
 * live metadata, so each tab's write undoes the other's. A pure function of
 * (item, this tab's live metadata) writes nothing, so there is nothing for a
 * peer to react to. The stored snapshot stays exactly as it was, which is also
 * what agents and every server-side reader of the doc projection keep seeing.
 * tests/unit/workspaces/item-label-follows-rename.test.ts mounts two clients
 * on one shared doc and requires zero doc updates.
 */

import { useEffect, useRef } from 'react';
import { fetchContentInfo } from './content-info-client';
import {
  useWorkspaceActions,
  useWorkspaceItems,
  useWorkspaceState,
  type WorkspaceContentCache,
  type WorkspaceStoreState,
} from './store';
import type { ScreenEntry } from '@/lib/workspaces/screens';
import type { WorkspaceItemDTO, WorkspaceItemType } from '@/lib/workspaces/types';

/**
 * This tab's live content metadata, keyed by content id. Structurally the
 * store's `contentCache`; only `displayName` is read here.
 */
export type LiveContentMeta = Readonly<
  Record<string, Pick<WorkspaceContentCache, 'displayName'> | undefined>
>;

/** No live metadata yet: every label resolves from the stored snapshot. */
export const NO_LIVE_CONTENT_META: LiveContentMeta = Object.freeze({});

/** What the rail puck, its hover card, the Stage reel and the dock call an
 *  item that has no name at all. Never shown for an item that has one. */
export const UNNAMED_ITEM_LABEL = 'Item';

/**
 * What a label is resolved from. A `WorkspaceItemDTO` satisfies it, and so
 * does a clipboard envelope row, which carries no item id.
 */
export type LabelSource = {
  type: WorkspaceItemType;
  refId: string | null | undefined;
  state?: { cache?: Record<string, unknown> | null } | null;
};

/** A usable name: a string with something visible in it. The shared doc is
 *  written by every editor, so a non-string or a blank value is skipped
 *  rather than rendered as "[object Object]" or as an empty label. */
function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Own keys only: ids come out of the shared doc or a window event, and a
 *  value such as "constructor" must not resolve to Object.prototype. */
function ownEntry<T>(map: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

/**
 * The live name for an item, or `null` while none has arrived. Only `content`
 * items reference a content row: an agent's `refId` names a user and a post's
 * names a post, so they never consult the content-keyed map.
 */
export function liveItemName(
  item: LabelSource,
  live: LiveContentMeta,
): string | null {
  if (item.type !== 'content' || !item.refId) return null;
  return nonEmptyString(ownEntry(live, item.refId)?.displayName);
}

/** The name snapshotted into the shared item when its window was created. */
export function storedItemName(item: LabelSource): string | null {
  const cache = item.state?.cache;
  if (!cache) return null;
  return (
    nonEmptyString(cache.displayName) ??
    nonEmptyString(cache.title) ??
    nonEmptyString(cache.username)
  );
}

/**
 * The label for one item: its live name, else its stored name, else
 * `fallback`. The fallback stays per surface (the rail says "Item", the
 * switcher names the type) because it only ever shows for an item that has no
 * name at all; every item that HAS a name reads the same everywhere.
 */
export function resolveItemLabel(
  item: LabelSource,
  live: LiveContentMeta,
  fallback: string,
): string {
  return liveItemName(item, live) ?? storedItemName(item) ?? fallback;
}

/**
 * The label for a screen entry, with the live-first rule applied to every
 * window on it. This is `screenEntryLabel` (lib/workspaces/screens.ts) with
 * the item lookup swapped for `resolveItemLabel`, and a blank screen's sheet
 * title resolved by `resolveSheetLabel` below; `screenEntryLabel` still reads
 * the stored snapshot for its callers outside this module. A parity test pins
 * the two to the same output when no live metadata exists, so the "A + B"
 * split format cannot drift between them.
 */
export function resolveScreenEntryLabel(
  entry: ScreenEntry,
  items: WorkspaceItemDTO[],
  live: LiveContentMeta,
): string {
  if (entry.kind === 'blank') return resolveSheetLabel(entry, items, live);
  const names = entry.memberIds.map((id) => {
    const it = items.find((x) => x.id === id);
    return it ? resolveItemLabel(it, live, it.type) : 'App';
  });
  return names.slice(0, 2).join(' + ') + (names.length > 2 ? ' +' : '');
}

// ── Screens named after the app that opened them ────────────────────────────
//
// WHY (the review of the 2026-09-13 pass). Opening an app with the then
// DEFAULT placement ("Open in a new screen", a setting retired on 2026-09-29)
// gave it a real screen: `addItem` minted a backing sheet under the window and
// titled it with the app's name AT OPEN TIME (`uniqueScreenName` in
// components/workspace/store.tsx: "Revent", or "Revent 2" when a sheet
// already had that name). Opening an app stores no sheet any more (the Stage
// algorithm, rule 1), but those sheets are still in people's workspaces as
// kept screens, and a client from before the change still mints them during
// a deploy, so this reading stays. The window folds into that sheet's entry, so the
// entry is `blank`, and this function used to return the stored sheet title
// for it on the premise that a sheet title is one the person sets. So in the
// default flow the desktop overview, Go to screen, Move to screen, the
// edge-swipe hint, the Stage screen card and the switcher strip all kept
// saying "Revent" beside a rail that said "Revent".
//
// A title `addItem` wrote has a shape the store can only have produced one
// way: it is a member window's STORED name, or that name plus the " N"
// suffix. Such a title is read as "named after this window" and follows the
// window's live name, suffix kept. Any other title is one a person chose (the
// overview's rename row, a "Screen 3" sheet, an app screen renamed into a
// sheet) and is shown exactly as stored. A person who renames a screen to the
// very name its app had is indistinguishable from the default and follows the
// app; that is the one trade this makes.
//
// Read-side, like everything here: the sheet's title in the shared doc is
// never rewritten, for the reason at the top of this file.

/** What `screenEntryLabel` calls a sheet with no title. */
const UNTITLED_SCREEN_LABEL = 'Blank screen';

/**
 * The " N" `uniqueScreenName` appends to a taken name: 2 through 499, with no
 * leading zero. A title ending in any other number is not its output.
 */
const UNIQUE_SCREEN_NAME_SUFFIX = /^ (?:[2-9]|[1-9]\d|[1-4]\d\d)$/;

/**
 * A blank screen's name: its sheet title, or, when that title is the default
 * a window on the screen was given at open time, that window's live name with
 * the same suffix. An exact match beats a suffixed one, and the first member
 * in screen order wins a tie, so the result never depends on map order.
 */
function resolveSheetLabel(
  entry: ScreenEntry,
  items: WorkspaceItemDTO[],
  live: LiveContentMeta,
): string {
  const sheet = items.find((it) => it.id === entry.id);
  // `screenEntryLabel` reads the same field; a non-string is skipped rather
  // than thrown on, since the shared doc is written by every editor.
  const title = nonEmptyString(sheet?.state.cache?.title)?.trim();
  if (!title) return UNTITLED_SCREEN_LABEL;
  let suffixed: string | null = null;
  for (const id of entry.memberIds) {
    if (id === entry.id) continue; // the sheet itself
    const member = items.find((it) => it.id === id);
    if (!member) continue;
    const liveName = liveItemName(member, live);
    const stored = storedItemName(member)?.trim();
    if (!liveName || !stored) continue;
    if (title === stored) return liveName;
    const rest = title.slice(stored.length);
    if (suffixed === null && title.startsWith(stored) && UNIQUE_SCREEN_NAME_SUFFIX.test(rest)) {
      suffixed = `${liveName}${rest}`;
    }
  }
  return suffixed ?? title;
}

/**
 * The title an APP window's Share row hands the share sheet. That row links to
 * the DOCUMENT open in the app (`/w/<projectFolderId>`, never the app's catalog
 * row; see workspace-canvas.tsx), so the share is named after the document:
 * its live name when this tab has one, else the title the app published for it
 * (`state.docTitle`, written by `workspace.set-title` in widget-bridge.tsx),
 * and the app's own label only when neither exists. It used to be the app's
 * name, so a "Tech week agenda" link went out titled "Revent".
 */
export function resolveAppDocumentTitle(
  item: WorkspaceItemDTO,
  live: LiveContentMeta,
  fallback: string,
): string {
  const documentId = nonEmptyString(item.state.projectFolderId);
  const liveDocument = documentId
    ? nonEmptyString(ownEntry(live, documentId)?.displayName)
    : null;
  return (
    liveDocument ??
    nonEmptyString(item.state.docTitle) ??
    resolveItemLabel(item, live, fallback)
  );
}

// Module scope, so `useWorkspaceState` sees one stable selector (it keys its
// subscription on the store only and treats the selector as constant).
const selectContentCache = (s: WorkspaceStoreState) => s.contentCache;

/**
 * This tab's live content metadata. Re-renders the caller only when
 * `seedContent` actually replaces the slice (an unchanged seed keeps the
 * reference). Reads the store and nothing else: no store write, no doc write.
 */
export function useLiveContentMeta(): LiveContentMeta {
  return useWorkspaceState(selectContentCache);
}

// ── Renames made in this tab ────────────────────────────────────────────────
//
// WHY. The live cache used to be filled only by the server bundle and by a
// ContentNode's mount-time `/info`, so a rename made in an open tab (the tile's
// settings, the Content panel, an app's Manage drawer, and an app renaming
// its saved document from its own title field, which reaches the server as a
// `documents.save` through widget-bridge.tsx) changed the content row and
// nothing the labels read: every surface above kept the old name until a
// reload (same 2026-09-13 pass). A rename path now announces the name the
// server accepted, and the one `LiveContentRenameSync` each workspace canvas
// mounts folds it into THIS tab's `contentCache`. Still read-side for the
// shared document: the announcement travels on this window only, the cache it
// writes is per-client store state, and `item.state.cache` is never touched.
// A tab that did not make the rename keeps what it knows until its own next
// fetch, exactly as before.
//
// A window event rather than a store call, so a rename path needs no store
// handle at all (the widget bridge, a drawer, a panel), and every workspace
// store in the tab hears the same fact about the content row.

/** Fired on `window` by a rename path after the server accepted the name. */
export const CONTENT_RENAMED_EVENT = 'workspace:content-renamed';

export type ContentRenamedDetail = { contentId: string; displayName: string };

/**
 * Tell this tab's workspace labels that content row `contentId` is now called
 * `displayName`. Call it only once the rename request has succeeded: the name
 * is shown as the row's live name, so a refused rename must not announce.
 * Trims the way `PATCH /api/content/[id]` stores it; a blank name or id
 * announces nothing.
 */
export function announceContentRenamed(contentId: string, displayName: string): void {
  if (typeof window === 'undefined') return;
  const name = typeof displayName === 'string' ? displayName.trim() : '';
  if (typeof contentId !== 'string' || contentId === '' || name === '') return;
  window.dispatchEvent(
    new CustomEvent<ContentRenamedDetail>(CONTENT_RENAMED_EVENT, {
      detail: { contentId, displayName: name },
    }),
  );
}

/**
 * The cache entry a rename produces when this tab already holds one for the
 * row: the same entry with the new name, every other field as it was. `null`
 * when there is no entry to fold the name into, since inventing the rest of a
 * row (its mime type above all) would mis-paint a ContentNode that mounts
 * from the cache before its own `/info` returns.
 */
export function renamedLiveEntry(
  cache: Readonly<Record<string, WorkspaceContentCache>>,
  contentId: string,
  displayName: string,
): WorkspaceContentCache | null {
  const prev = ownEntry(cache, contentId);
  return prev ? { ...prev, displayName } : null;
}

/** A whole cache entry from an `/info` payload, mapped the way ContentNode
 *  seeds it, keyed by the id the workspace items reference. */
function liveEntryFromInfo(
  contentId: string,
  announcedName: string,
  info: Record<string, unknown>,
): WorkspaceContentCache {
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  return {
    id: contentId,
    displayName: nonEmptyString(info.displayName) ?? announcedName,
    contentType: str(info.contentType) ?? 'document',
    mimeType: str(info.mimeType) ?? 'application/octet-stream',
    thumbnailPath: str(info.thumbnailPath),
    byteSize: num(info.byteSize) ?? num(info.size),
    updatedAt: str(info.updatedAt),
  };
}

function readRenamedDetail(event: Event): ContentRenamedDetail | null {
  const detail = (event as CustomEvent<unknown>).detail as
    | Partial<Record<keyof ContentRenamedDetail, unknown>>
    | null
    | undefined;
  if (!detail || typeof detail !== 'object') return null;
  const { contentId, displayName } = detail;
  if (typeof contentId !== 'string' || contentId === '') return null;
  const name = nonEmptyString(displayName);
  return name ? { contentId, displayName: name.trim() } : null;
}

/**
 * Applies `announceContentRenamed` to this store's live content cache. Mount
 * exactly once per workspace store; WorkspaceCanvas does. Renders nothing.
 *
 * An entry this tab already holds takes the new name at once. A row with no
 * entry yet but a window on this workspace (an app opened after the page
 * loaded whose tile never mounted, a docked window) is refetched through the
 * coalesced `/info` client so its label can follow too. So is the document an
 * app window has open (`state.projectFolderId`): the server bundle seeds only
 * `refId` rows, and without the refetch an app window's Share title
 * (`resolveAppDocumentTitle`) could never learn that its document was renamed
 * in this tab. A row nothing here references is ignored. The only write is
 * `seedContent`, per-client store state; nothing reaches the shared document.
 */
export function LiveContentRenameSync(): null {
  const { seedContent } = useWorkspaceActions();
  const cache = useWorkspaceState(selectContentCache);
  const items = useWorkspaceItems();
  // Refs so the listener binds once per store and still reads the latest
  // slice at event time (the canvas's `screensItemsRef` pattern).
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    let disposed = false;
    const onRenamed = (event: Event) => {
      const detail = readRenamedDetail(event);
      if (!detail) return;
      const { contentId, displayName } = detail;
      const next = renamedLiveEntry(cacheRef.current, contentId, displayName);
      if (next) {
        seedContent(next);
        return;
      }
      const referenced = itemsRef.current.some(
        (it) =>
          it.type === 'content' &&
          (it.refId === contentId || it.state.projectFolderId === contentId),
      );
      if (!referenced) return;
      fetchContentInfo(contentId)
        .then((info) => {
          if (disposed || !info) return;
          seedContent(liveEntryFromInfo(contentId, displayName, info));
        })
        .catch(() => {
          // Unreadable now: the label keeps its stored name until the next
          // page load seeds the row, which is where it was before the rename.
        });
    };
    window.addEventListener(CONTENT_RENAMED_EVENT, onRenamed);
    return () => {
      disposed = true;
      window.removeEventListener(CONTENT_RENAMED_EVENT, onRenamed);
    };
  }, [seedContent]);

  return null;
}
